import { WEB_VERSION_HEADER, type ApiError, type CaseView } from '@chemoflow/contracts';
import { noteServerVersion } from '../lib/appVersion.js';

/** 更新APIで必須の独自ヘッダ。CSRF対策としてサーバ側で照合する。 */
const REQUEST_HEADER = 'x-chemoflow-request';

export class ApiFailure extends Error {
  constructor(
    readonly code: ApiError['error']['code'],
    message: string,
    readonly current?: CaseView,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

async function parseError(res: Response): Promise<ApiFailure> {
  try {
    const body = (await res.json()) as ApiError;
    return new ApiFailure(body.error.code, body.error.message, body.error.current, res.status);
  } catch {
    return new ApiFailure('internal', '通信できませんでした', undefined, res.status);
  }
}

/**
 * APIの呼び出し口。
 * ・患者表示情報を含む応答をブラウザ・中間装置へ残さない（no-store）
 * ・Cookieは同一オリジンのみ
 * ・失敗は ApiFailure に正規化し、画面側で競合と通信断を区別できるようにする
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const res = await fetch(path, {
    ...init,
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(method === 'GET' || method === 'HEAD' ? {} : { [REQUEST_HEADER]: '1' }),
      ...init.headers,
    },
  });
  // 配備後の古い画面かどうか（lib/appVersion.ts）
  noteServerVersion(res.headers.get(WEB_VERSION_HEADER));
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const post = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const put = <T>(path: string, body: unknown): Promise<T> =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });

/** 再送時の二重反映を防ぐためのリクエストID。 */
export const newRequestId = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
